/**
 * PostVersions Component for LOOPINO AI Platform
 * Displays a slider with post version thumbnails
 */

import React, { useCallback, useMemo, useRef } from 'react';
import './DesktopPostVersions.css';
import placeholderImage from '../../../../assets/images/placeholder-post-image.png';
import useLanguage from '../../../../hooks/useLanguage';
import DesktopHorizontalScroll from '../../common/scroll/DesktopHorizontalScroll';

const DesktopPostVersions = ({
  versions = [],
  currentVersionIndex = 0,
  definitiveVersionIndex = undefined,
  onVersionSelect,
  onSetDefinitiveVersion,
  isSettingDefinitive = false,
  parentPost, // Need parent post to get media URLs
  postSwipeHandlers = null,
  getImageLoadingProps = null,
  imageLoadingScope = 'versions'
}) => {
  const { t, language } = useLanguage();
  const isSliderTouchActiveRef = useRef(false);

  const resolveImageLoadingProps = useCallback((source, suffix = 'thumbnail') => {
    if (typeof getImageLoadingProps !== 'function') {
      return null;
    }

    const scopedSuffix = typeof suffix === 'string' || typeof suffix === 'number'
      ? suffix
      : 'thumbnail';
    const scopeKey = `${imageLoadingScope}-${scopedSuffix}`;
    return getImageLoadingProps(source, scopeKey);
  }, [getImageLoadingProps, imageLoadingScope]);

  // Handle thumbnail click
  const handleVersionClick = useCallback((index) => {
    if (index !== currentVersionIndex && onVersionSelect) {
      onVersionSelect(index);
    }
  }, [currentVersionIndex, onVersionSelect]);

  const handleVersionKeyDown = useCallback((event, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleVersionClick(index);
    }
  }, [handleVersionClick]);

  // Handle image error
  const handleImageError = useCallback((e) => {
    if (e?.target) {
      e.target.onerror = null;
      e.target.src = placeholderImage;
    }
  }, []);

  // Format version date - support multiple timestamp formats
  const formatVersionDate = (version) => {
    // Try different timestamp field names
    const timestamp = version?.timestamp ||
                      version?.created_at ||
                      version?.createdAt ||
                      version?.generated_at ||
                      version?.generatedAt;

    if (!timestamp) return null;

    try {
      // Handle MongoDB date format { $date: "..." }
      const dateValue = typeof timestamp === 'object' && timestamp.$date
        ? timestamp.$date
        : timestamp;

      const date = new Date(dateValue);

      // Check if date is valid
      if (isNaN(date.getTime())) return null;

      const locale = language || 'it';
      return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('[PostVersions] Error formatting date:', error);
      return null;
    }
  };

  // Get thumbnail URL for a version
  const getVersionThumbnail = (version, parentPost) => {
    // First try to get media from the version itself (versions can have their own images)
    const versionMedia = version?.media_urls || version?.images || [];
    if (versionMedia && versionMedia.length > 0) {
      const firstMedia = versionMedia[0];
      return firstMedia?.url || firstMedia;
    }

    // Fallback to parent post media if version doesn't have its own
    const parentMedia = parentPost?.media_urls || parentPost?.images || [];
    if (parentMedia && parentMedia.length > 0) {
      const firstMedia = parentMedia[0];
      return firstMedia?.url || firstMedia;
    }

    // Final fallback to placeholder
    return placeholderImage;
  };

  const stopEvent = (event) => {
    // Always stop propagation so the parent swipe handler never sees slider touches
    event.stopPropagation();
  };

  const handleSliderTouchStart = useCallback((event) => {
    isSliderTouchActiveRef.current = true;
    postSwipeHandlers?.reset?.();
    stopEvent(event);
  }, [postSwipeHandlers]);

  const handleSliderTouchMove = useCallback((event) => {
    if (!isSliderTouchActiveRef.current) {
      return;
    }

    stopEvent(event);
  }, []);

  const handleSliderTouchEnd = useCallback((event) => {
    if (!isSliderTouchActiveRef.current) {
      return;
    }

    isSliderTouchActiveRef.current = false;
    stopEvent(event);
  }, []);

  const handleSliderTouchCancel = useCallback((event) => {
    if (!isSliderTouchActiveRef.current) {
      return;
    }

    isSliderTouchActiveRef.current = false;
    stopEvent(event);
  }, []);

  const handleContainerTouchStart = useCallback((event) => {
    postSwipeHandlers?.onTouchStart?.(event);
  }, [postSwipeHandlers]);

  const handleContainerTouchMove = useCallback((event) => {
    postSwipeHandlers?.onTouchMove?.(event);
  }, [postSwipeHandlers]);

  const handleContainerTouchEnd = useCallback((event) => {
    postSwipeHandlers?.onTouchEnd?.(event);
  }, [postSwipeHandlers]);

  const handleContainerTouchCancel = useCallback((event) => {
    postSwipeHandlers?.onTouchEnd?.(event);
  }, [postSwipeHandlers]);

  const touchEventProps = {
    onTouchStart: handleSliderTouchStart,
    onTouchMove: handleSliderTouchMove,
    onTouchEnd: handleSliderTouchEnd,
    onTouchCancel: handleSliderTouchCancel
  };

  // Don't render if no versions or only one version
  if (!versions || versions.length === 0) {
    return null;
  }

  const currentVersion = versions[currentVersionIndex];
  const totalVersions = versions.length;
  const versionNumber = totalVersions - currentVersionIndex;
  const currentVersionDate = useMemo(() => formatVersionDate(currentVersion), [currentVersion, language]);
  const isCurrentDefinitive = definitiveVersionIndex !== undefined && definitiveVersionIndex === currentVersionIndex;

  const selectButtonLabel = isSettingDefinitive
    ? t('postReview.versions.actions.saving')
    : isCurrentDefinitive
      ? t('postReview.versions.actions.active')
      : t('postReview.versions.actions.select');

  return (
    <div className="loopino-desktop-post-versions-container">
      <div
        className="loopino-desktop-post-versions-card"
        onTouchStart={handleContainerTouchStart}
        onTouchMove={handleContainerTouchMove}
        onTouchEnd={handleContainerTouchEnd}
        onTouchCancel={handleContainerTouchCancel}
      >
        {/* Header with version info */}
        <div className="loopino-desktop-post-versions-header">
          <h4 className="loopino-desktop-post-versions-title">
            {t('postReview.versions.header.title', { number: versionNumber, total: totalVersions })}
          </h4>
          {currentVersionDate && (
            <span className="loopino-desktop-post-versions-date">
              {currentVersionDate}
            </span>
          )}
        </div>

        {/* Thumbnails slider */}
        <div className="loopino-desktop-post-versions-thumbnails">
          <DesktopHorizontalScroll
            trackClassName="loopino-desktop-post-versions-slider"
            padding="16px"
            trackProps={touchEventProps}
          >
            {versions.map((version, index) => {
              const displayNumber = totalVersions - index;
              const isActive = index === currentVersionIndex;
              const isDefinitive = definitiveVersionIndex !== undefined && index === definitiveVersionIndex;
              const thumbnailUrl = getVersionThumbnail(version, parentPost);
              const versionIdentifier = version?.version_id || version?.id || version?._id || index;
              const blurProps = resolveImageLoadingProps(thumbnailUrl, versionIdentifier);

              return (
                <div
                  key={version.version_id || version?.id || version?._id || index}
                className={`loopino-desktop-post-version-thumbnail ${isActive ? 'loopino-desktop-active' : ''} ${isDefinitive ? 'loopino-desktop-definitive' : ''}`}
                  onClick={() => handleVersionClick(index)}
                  onKeyDown={(event) => handleVersionKeyDown(event, index)}
                  role="button"
                  tabIndex={0}
                  aria-label={[
                    t('postReview.versions.aria.version', { number: displayNumber }),
                    isDefinitive ? t('postReview.versions.aria.definitive') : '',
                    isActive ? t('postReview.versions.aria.selected') : ''
                  ].filter(Boolean).join(' ')}
                  aria-current={isActive ? 'true' : undefined}
                >
                  <img
                    src={thumbnailUrl}
                    alt={t('postReview.versions.images.alt', { number: displayNumber })}
                    className={[
                      'loopino-desktop-post-version-thumbnail-image',
                      blurProps ? 'loopino-desktop-image-with-loading' : '',
                      blurProps?.className || ''
                    ].filter(Boolean).join(' ')}
                    onLoad={(event) => {
                      blurProps?.onLoad?.(event);
                    }}
                    onError={(event) => {
                      blurProps?.onError?.(event);
                      handleImageError(event);
                    }}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              );
            })}
          </DesktopHorizontalScroll>
        </div>

        {/* Button to set definitive version */}
        {onSetDefinitiveVersion && (
          <div className="loopino-desktop-post-versions-button-container">
            <button
              className="loopino-desktop-post-versions-select-button"
              onClick={onSetDefinitiveVersion}
              disabled={isSettingDefinitive || isCurrentDefinitive}
            >
              {isSettingDefinitive ? (
                <>
                  <div className="loopino-desktop-post-versions-spinner" />
                  {t('postReview.versions.actions.saving')}
                </>
              ) : (
                selectButtonLabel
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DesktopPostVersions;
